import { createThunk, model, Model } from 'mst-collection';
import { types } from 'mobx-state-tree';

import {
  Api,
  QuestStatusEnum,
  FollowSuggestionStatusEnum,
} from '@api';
import {
  LanguageCodesEnum,
  NotificationSettingsTypeEnum,
} from '@constants';
import { enumerationNumber } from '../types';
import { ThunkAbortController } from '../utils';

export class UserSettings extends Model({
  language: types.enumeration<LanguageCodesEnum>(
    Object.values(LanguageCodesEnum),
  ),
  hideLocation: types.boolean,
  notifyNewTracks: types.boolean,
  notifyNewVideos: types.boolean,
  notifyNewMentions: types.boolean,
  notifyNewMessages: types.boolean,
  skillLevel: types.maybeNull(types.number),
  questStatus: enumerationNumber('QuestStatus', [
    QuestStatusEnum.NeedToAsk,
    QuestStatusEnum.Done,
    QuestStatusEnum.Skipped,
    QuestStatusEnum.NeverAsk,
  ]),
  followStatus: enumerationNumber('FollowStatus', [
    FollowSuggestionStatusEnum.NeedToAsk,
    FollowSuggestionStatusEnum.Done,
    FollowSuggestionStatusEnum.Skipped,
    FollowSuggestionStatusEnum.NeverAsk,
  ]),
}) {
  private _toggleNotificationsAbortController =
    new ThunkAbortController();

  get notifyAll() {
    return (
      this.notifyNewTracks &&
      this.notifyNewVideos &&
      this.notifyNewMentions &&
      this.notifyNewMessages
    );
  }

  get isNeedToPersonalize(): boolean {
    return (
      this.isNeedToAskSkillLevel ||
      this.isNeedToAskQuest ||
      this.isNeedToSuggestUsers
    );
  }

  get isNeedToAskSkillLevel() {
    return this.skillLevel === null;
  }

  get isNeedToAskQuest() {
    return (
      this.questStatus === QuestStatusEnum.NeedToAsk ||
      this.questStatus === QuestStatusEnum.Skipped
    );
  }

  get isSkippedQuest() {
    return this.questStatus === QuestStatusEnum.Skipped;
  }

  get isNeedToSuggestUsers() {
    return (
      this.followStatus === FollowSuggestionStatusEnum.NeedToAsk ||
      this.followStatus === FollowSuggestionStatusEnum.Skipped
    );
  }

  setQuestStatus(status: QuestStatusEnum) {
    this.questStatus = status;
  }

  setSkillLevel(value: number) {
    this.skillLevel = value;
  }

  updateNotifications(type: NotificationSettingsTypeEnum) {
    switch (type) {
      case NotificationSettingsTypeEnum.All: {
        if (this.notifyAll) {
          this.notifyNewTracks = false;
          this.notifyNewVideos = false;
          this.notifyNewMentions = false;
          this.notifyNewMessages = false;
        } else {
          this.notifyNewTracks = true;
          this.notifyNewVideos = true;
          this.notifyNewMentions = true;
          this.notifyNewMessages = true;
        }
        break;
      }
      case NotificationSettingsTypeEnum.NewTracks: {
        this.notifyNewTracks = !this.notifyNewTracks;
        break;
      }
      case NotificationSettingsTypeEnum.NewVideos: {
        this.notifyNewVideos = !this.notifyNewVideos;
        break;
      }
      case NotificationSettingsTypeEnum.NewMentions: {
        this.notifyNewMentions = !this.notifyNewMentions;
        break;
      }
      case NotificationSettingsTypeEnum.NewMessages: {
        this.notifyNewMessages = !this.notifyNewMessages;
        break;
      }
    }
  }

  toggleNotifications = createThunk(
    (type: NotificationSettingsTypeEnum) => {
      return async function toggleNotifications(this: UserSettings) {
        try {
          // Cancel previous request if it in progress
          this._toggleNotificationsAbortController.abort();

          this.updateNotifications(type);

          await Api.Profile.updateProfile(
            {
              settings: {
                notifyNewTracks: this.notifyNewTracks,
                notifyNewVideos: this.notifyNewVideos,
                notifyNewMentions: this.notifyNewMentions,
                notifyNewMessages: this.notifyNewMessages,
              },
            },
            this._toggleNotificationsAbortController.create(),
          );

          this._toggleNotificationsAbortController.remove();
        } catch (error) {
          // If request was canceled do nothing
          if (ThunkAbortController.isCanceled(error)) {
            return;
          }

          // Change all notification values back
          this.updateNotifications(type);

          throw error;
        }
      };
    },
  );
}

export const UserSettingsModel = model(UserSettings);
